import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toOptions } from '@/shared/lib/options';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { toSearchParams } from '@/shared/lib/search';
import { useListFilters } from '@/shared/lib/useListFilters';
import { FilterActions } from '@/shared/ui/FilterActions';
import { FilterField } from '@/shared/ui/FilterField';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { SearchKeyInput } from '@/shared/ui/SearchKeyInput';
import { SortableTh } from '@/shared/ui/SortableTh';
import { fetchCarTypes, fetchSeoBrands, type SeoBrand } from './api';
import { OnlySeoActiveFilter, SitemapToggleCell } from './SitemapToggle';

const LIST_KEY = 'seo-brands';

const SEARCH_KEY_OPTIONS = [
  { value: 'id', label: 'Поиск по id' },
  { value: 'name', label: 'Поиск по марке' },
];

const emptyFilters = { searchKey: '', searchValue: '', typeId: '' };

export const SeoBrandsPage = () => {
  const list = useListFilters(emptyFilters);
  const { draft, applied, page, sortField, sortOrder } = list;
  const [onlyActive, setOnlyActive] = useState(false);

  const types = useQuery({ queryKey: ['car-types'], queryFn: fetchCarTypes });

  const params = {
    page,
    sort_field: sortField,
    sort_order: sortOrder,
    ...toSearchParams(applied.searchKey, applied.searchValue),
    type_id: applied.typeId,
    is_seo_active: onlyActive ? (1 as const) : undefined,
  };
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [LIST_KEY, params],
    queryFn: () => fetchSeoBrands(params),
  });
  const rows = getListRows<SeoBrand>(data, ['marks']);

  return (
    <>
      <section className="content-header">
        <h1>Марки</h1>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">Марка</h3>
            <form onSubmit={list.submit} style={{ marginTop: 12 }}>
              <FilterField label="Поиск">
                <SearchKeyInput
                  keyOptions={SEARCH_KEY_OPTIONS}
                  searchKey={draft.searchKey}
                  searchValue={draft.searchValue}
                  onKeyChange={(v) => list.setField('searchKey', v)}
                  onValueChange={(v) => list.setField('searchValue', v)}
                />
              </FilterField>
              <FilterField label="Фильтрация по типу">
                <FilterSelect
                  options={toOptions(types.data)}
                  value={draft.typeId}
                  isLoading={types.isLoading}
                  onChange={(v) => list.setField('typeId', v)}
                />
              </FilterField>
              <FilterActions disabled={isFetching} onReset={list.reset} />
            </form>
          </div>
          <div className="box-header with-border">
            <OnlySeoActiveFilter
              checked={onlyActive}
              onChange={(checked) => {
                setOnlyActive(checked);
                list.setPage(1);
              }}
            />
          </div>
          <div className="box-body">
            <QueryStatus isLoading={isLoading} error={error} errorText="Ошибка загрузки марок" />
            <table className="table table-bordered">
              <thead>
                <tr>
                  <SortableTh active={sortField === 'id'} onClick={() => list.toggleSort('id')}>
                    id
                  </SortableTh>
                  <SortableTh active={sortField === 'name'} onClick={() => list.toggleSort('name')}>
                    Марка
                  </SortableTh>
                  <SortableTh
                    active={sortField === 'type_id'}
                    onClick={() => list.toggleSort('type_id')}
                  >
                    Тип
                  </SortableTh>
                  <th>Участвует в структуре сайта</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((brand) => (
                  <tr key={brand.id}>
                    <td>{brand.id}</td>
                    <td>{brand.name}</td>
                    <td>{brand.type || 'No type name'}</td>
                    <td>
                      <SitemapToggleCell
                        entity="brand"
                        id={brand.id}
                        isActive={Boolean(brand.is_seo_active)}
                        listKey={LIST_KEY}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalPages={getTotalPages(data)} onChange={list.setPage} />
          </div>
        </div>
      </section>
    </>
  );
};
